'use strict';
import _ from 'lodash';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  ViewRef
} from '@angular/core';
import moment from 'moment';
import { BehaviorSubject, Observable } from 'rxjs';
import { LazyLoadEvent } from 'primeng/api/lazyloadevent';
import { Paginator } from 'primeng/paginator';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs/Subject';
import { Table, TableHeaderCheckbox } from 'primeng/table';
import { debounceTime, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { of } from 'rxjs/observable/of';

import {
  BULK_OPERATION_STATUS,
  IKeyElement,
  ITablelistAction,
  ITablelistExecute,
  ITablelistRequester,
  ITablelistStateListItem,
  ITablelistStateListItemConfig,
  ResourceTypes,
  TableItemInterface
} from '../../interfaces';
import { AdaptiveFilterService } from '@ispm/shared/services/adaptive-filter.service';
import { AuthenticationService } from '@ispm/user/services/authentication.service';
import { BulkOperationService } from '../../../bulk-operation/services/bulk-operation.service';
import { CURRENT_ORDER_ACTIONS } from '@ispm/dashboard/test/constants/current-order.constants';
import { ConditionalItem } from '@ispm/shared/classes/conditional-item.class';
import { ConfirmModalComponent } from '@ispm/layout/components/modals/confirm-modal/confirm-modal.component';
import { ConfirmModalLoaderPayload } from '@ispm/layout/classes/confirm-modal-loader-payload';
import { CoreActionCreatorService } from "@ispm/core/services/core-action-creator.service";
import { DashboardService } from '../../services/dashboard.service';
import { DataMigrationService } from "@ispm/shared/services/data-migration.service";
import { DebounceThrottleUtils } from '@ispm/shared/classes/debounce-throttle-utils';
import { DynamicApiService } from '../../../export/services/dynamic-api.service';
import { EXPORT_DATA_STORAGE_KEY } from '@ispm/shared/services/ui-customization.service';
import { EmrIntegrationService } from '@ispm/dashboard/test/services/emr-integration.service';
import { ExtendedHttpResponse } from "@ispm/core/services/http-interceptor.service";
import { FilterService } from '../../../filter/services/filter.service';
import { InjectedActionsFactoryService } from '../../services/injected-actions-factory.service';
import { LogService } from '@ispm/core/services/log.service';
import { ModalTypes } from '@ispm/layout/components/modals/contants/modal-types.enum';
import { NavigationConfigService } from '@ispm/dashboard/navigation/services/navigationConfig.service';
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';
import { PopupActionComponent } from './popup-action/popup-action.component';
import { RetriggerUserActivationService } from "@ispm/shared/services/retrigger-user-activation.service";
import { SIMPLE_QUERY } from '../../services/simple-search.service';
import { SearchService } from "@ispm/dynamic-form/services/search.service";
import { RouterManagerService } from '@ispm/core/services/router-manager.service';
import { SessionStorageService } from '@ispm/shared/services/session-storage.service';
import { SimpleSearchOrigin } from '../../interfaces/simple-search.interface';
import { StringUtils } from '@ispm/shared/utils/String.utils';
import { TablelistDataHandlerService } from '../../services/tablelist-data-handler.service';
import { TablelistHandlerService } from '../../services/tablelist-handler.service';
import { TablelistStoreHandlerService } from '../../services/tablelist-store-handler.service';
import { USER_ACTIONS } from '@ispm/user/store/user-actions.constant';
import { UserActionCreatorService } from "@ispm/user/services/user-action-creator.service";
import { WorkflowListTableState } from '../../interfaces/workflow-list-table-state';
import { WorkflowService } from "@ispm/workflow/services/workflow.service";
import { WorkflowUtilService } from "@ispm-client/modules/aggregator/services";
import { WorklistActionsComponent } from '../worklist-actions/worklist-actions.component';
import { WorklistDuplicatePatientsManager } from './worklist-helpers/worklist-duplicate-patients-manager';
import { WorklistHelper } from '../../worklist-helper';
import { WorklistService } from '../../services/worklist.service';
import { modalResponse } from '@ispm/layout/constants/modal-responses.enum';
import { ExportWorklistDataPollingService } from "../../../export/services/export-worklist-data-polling.service";
import { ExportWorklistDataService } from "../../../export/services/export-worklist-data.service";
import {NotesService} from "@ispm/user/services/notes.service";

const DEFAULT_SORT_ORDER: number = -1;
const DEFAULT_SORT_FIELD: string = 'revDatetime';
const WORKLIST_TYPE_PATIENT_LIST: string = 'patient';
const REASSIGN_BULK_OPERATION = 'REASSIGN-BULK-OPERATION';

@Component({
  selector: 'gen-worklist',
  templateUrl: './worklist.component.html',
  styleUrls: ['./worklist.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class WorklistComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('customPaginator') customPaginator: Paginator;
  @ViewChild('table', {static: false}) myTable: Table;
  @ViewChildren('row', {read: ElementRef}) rowElements: QueryList<ElementRef>;

  @Input()
  set resetWorklist(reset: boolean) {
    if (reset && this._isConfigLoaded) {
      this.resetFlags();
      this.setWorklist(true);
    }
  }

  @Input()
  set searchResource(text: string) {
    if (text !== this._searchResource) {
      this._searchResource = text ? text : '';
    }
  }

  get searchResource() {
    return this._searchResource;
  }

  @Input()
  set searchText(text: string) {
    if (text !== this._searchText && this._isConfigLoaded) {
      this.list = [];
      this._searchText = (this._resource && this._searchResource !== this._resource && !this.searchPropagation)
        ? ''
        : (text ? text : '');
      this.compactedSearchText = this.getUseExactMultiSearch()
        ? this.getCompactedSearchText(this._searchText)
        : this._searchText;
      this.resetFlags();
      this.setSelectedItem(false);

      if (this._searchText === '') {
        this.setWorklist(true, false);
        this.checkHeaderCheckBox();
      } else {
        if (this.getResourceType() === ResourceTypes.Generic) {
          this.lastEvent.first = 0;
          this.lastEvent.page = 1;
        }

        this._searchOperations.next(this._searchText);
      }
    }
  }

  get searchText() {
    return this._searchText;
  }

  @Input()
  set values(values: any[]) {
    this._values = values
      ? values.map(val => {
        return {...val, isNg2TestView: val && val.test_name && val.test_name.includes('Pathway')}; // TODO: LEV change to something smarter?
      })
      : [];
    // we reset the first page if this value been set / override
    this.lastEvent.first = 0;
    this.lastEvent.page = 0; // TODO check this value, before was 0
    this._previousPage = -1;
    // length property values will be filled for manual case under ngOnChanges itself : GEN-173606
    if (this.getResourceType() === ResourceTypes.Manual && this.values) {
      this.length = this.values.length;
    }
    this.loadMoreRows(this.lastEvent).then();
    this._changeDetector.detectChanges();
  }

  get values(): Array<any> {
    return this._searchText.length !== 0 && !!this._filteredValues ? this._filteredValues : (this._values || []);
  }

  @Input()
  get selectedRows() {
    return this._filterService.getSelectedRows().value;
  }

  set selectedRows(selectedRows) {
    this._filterService.setSelectedRows(selectedRows || []);
    if (this.selectedRows.length === 0 && sessionStorage.getItem(EXPORT_DATA_STORAGE_KEY)) {
      sessionStorage.removeItem(EXPORT_DATA_STORAGE_KEY);
    }
  }

  @Input()
  get mode() {
    return this._mode;
  }

  set mode(mode) {
    this._mode = mode || ResourceTypes.Manual;
  }

  @Input()
  set injectedActions(injectedActions: Array<ITablelistAction>) {
    this._injectedActions = injectedActions;
    this.setWorklistActions();
  }

  get injectedActions() {
    return this._injectedActions;
  }

  @Input()
  get trackByField() {
    return this._trackByField;
  }

  set trackByField(text: string) {
    this._trackByField = text;

    if (this._mode && this._mode.toLowerCase() !== ResourceTypes.Generic) {
      this._originalTrackBy = text;
    }
  }

  @Input()
  set load(load: boolean) {
    this.loading = load;
    this.initialized = !load;
    this._changeDetector.markForCheck();
  }

  @Input()
  get resource() {
    return this._resource;
  }

  set resource(resource) {
    if (resource && resource !== this._resource) {
      this._resource = resource;
    }
  }

  @Input()
  set appliedFilterObj(appliedFilterQueryObj: any) {
    this._appliedFilterObj = appliedFilterQueryObj;
  }

  get appliedFilterObj() {
    return this._appliedFilterObj;
  }

  get totalRecords$() {
    return this._totalRecords$;
  }

  @Output() bulkOperationDisabledStatus: EventEmitter<boolean> = new EventEmitter<boolean>();

  get showDuplicatePatientWarning(): boolean {
    return this._showDuplicatePatientWarning;
  }

  set showDuplicatePatientWarning(val: boolean) {
    this._showDuplicatePatientWarning = val;

    if (this._showDuplicatePatientWarning === false) { // change detection is needed when user clicks Back. If not, clicking again on the same patient will not show duplicate patient warning
      this._changeDetector.markForCheck();
      this._changeDetector.detectChanges();
    }
  }

  private set loadedItemsAllPages(value: any[]) {
    let selectAll = false;

    if (
      this.selectedRows && this._unselectedRowsIds
      && (this.selectedRows.length + this._unselectedRowsIds.length === this._loadedItemsAllPages.length)
    ) {
      selectAll = true;
    }

    this._loadedItemsAllPages = [...value];

    if (
      (value.length > 0) && (this.selectedRows && this.selectedRows.length > 0)
      && selectAll
      // && (value.length === this.selectedRows.length) &&
      // value.every((obj, idx) => _.isEqual(obj, this.selectedRows[idx]))
    ) {
      this.setSelectedItem(true);
    }
  }

  private get loadedItemsAllPages(): any[] {
    return this._loadedItemsAllPages;
  }

  @Input() actions = WorklistActionsComponent.ACTIONS;
  @Input() appliedFilterProperties: any;
  @Input() blockInitialLoad: boolean = false;
  @Input() checkActionsPermission: boolean = true;
  @Input() disabledWorklistActions: any[];
  @Input() displayCheckableItems: boolean = true;
  @Input() executeActions: ITablelistExecute[];
  @Input() isInfiniteTotalCounting: boolean = false; // in use for calculation pages. Audit component, for example, does not have total records count
  @Input() searchPropagation: boolean = true;
  @Input() showFooter = true;
  @Input() sortBy: string = DEFAULT_SORT_FIELD;
  @Input() titles: TableItemInterface[];
  @Input() worklistActions: any;

  @Output() noteCompletedEmit: EventEmitter<any> = new EventEmitter();
  @Output() onSearchEnd: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() pageChanged: EventEmitter<number> = new EventEmitter<number>();
  @Output() reloadTriggered: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() rowClicked: EventEmitter<any> = new EventEmitter<any>();
  @Output() selectAllRowsClicked: EventEmitter<any> = new EventEmitter<any>();

  public bulkOperationDisabled: boolean;
  public canExportData: boolean = false;
  public compactedSearchText: string = '';
  public currentPage: number;
  public currentUserId: string;
  public duplicatePatientsManager: WorklistDuplicatePatientsManager;
  public exportDataConfig: any = {};
  public hasTrackBy: boolean = null;
  public initialized: boolean = false;
  public lastEvent = {first: 0, rows: 20, page: 0};
  public length: number;
  public list: any[];
  public loading: boolean = true;
  public localConfig: ITablelistStateListItemConfig;
  public practitionerType: string;
  public requesterConfig: ITablelistRequester = {
    adaptableFilters: undefined,
    additionalResourcesToRetrieve: [],
    associationItems: null,
    configTemplateType: this.resource,
    dynamicSearch: null,
    ids: [],
    offset: 0,
    onSearchEndEvent: this.onSearchEnd,
    paginate: false,
    preventLazyLoad: false,
    queryOrigin: null,
    queryType: '',
    resetIds: false,
    resource: null,
    resourceType: ResourceTypes.Generic,
    searchTerm: '',
    simpleSearch: null,
    sortByField: null,
    sortByOrder: null,
    start: 0,
  };
  public rowsPerPageOptions: number[] = [10, 20, 30, 50];
  public sortKeys: string[];
  public sortOrder: number = DEFAULT_SORT_ORDER;
  public stateKey = SessionStorageService.WORKLIST_SESSION_STORAGE_NAME;
  public visible: boolean = true;

  private _allFieldsExportDataConfig: any = {};
  private _allowUpdatesList = false;
  private _appliedFilterObj: string = '';
  private _blockSortTrigger = false;
  private _buffer = [];
  private _checkIfRowExistsInNextPages: boolean = false;
  private _checkIfRowExistsInPreviousPages: boolean = false;
  private _isConfigLoaded = false;
  private _currentConfigTemplate: ITablelistStateListItemConfig = {
    actions: {
      disabled: [],
      labels: '',
    },
    id: '',
    name: '',
    numberOfChunkSizeForSearch: 1,
    resource: '',
    resourceTypeCode: this.mode,
    resources: [],
    sortByField: '',
    table: [],
    trackByField: 'id',
  };
  private _currentFieldsExportDataConfig: any = {};
  private _currentOffset: number;
  private _debounceEmitDataLoad = new DebounceThrottleUtils().debounceFunction(this.emitDataLoad, this, 250);
  private _destroyer$: Subject<any> = new Subject<any>();
  private _errorShown: boolean
  private _filterApplied: boolean = false;
  private _filteredValues: any;
  private _hasActionColumn: boolean = false;
  private _inTestLoading: boolean;
  private _injectedActions: Array<ITablelistAction> = [];
  private _injectedActionsCreated: boolean = false;
  private _keyTypes: string[] = ['date', 'text', 'tag', 'priority', 'array', 'arraycontent', 'boolean-indication', 'iconwithdata', 'appointment', 'arraycontenticon', 'arrayContentOrgList', 'textwithellipsis'];
  private _lastBulkOperationResponse: string;
  private _lastSort: any;
  private _loadedItemsAllPages: any[] = [];
  private _loadedResourceId = null;
  private _modalLoaderTunnelName: string = StringUtils.generateGuid();
  private _mode: string = ResourceTypes.Manual;
  private _numberOfChunkSizeForSearch = 1;
  private _originalTrackBy: string;
  private _previousPage = -1;
  private _resource: string = 'test';
  private _searchOperations: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  private _searchResource: string = '';
  private _searchText: string = '';
  private _searchableFields: Map<string, IKeyElement> = new Map();
  private _secondaryTrackByFields: string[];
  private _showDuplicatePatientWarning: boolean = false;
  private _sortTypes: any = {};
  private _tableState: WorkflowListTableState = {
    first: 0,
    rows: 20,
    selection: [],
    visitedRowId: null
  };
  private _totalRecords$: Observable<number>;
  private _trackByField: string = '';
  private _unselectedRowsIds: any[];
  private _values: any[];
  private _worklistTemplateId: string;

  constructor(
    // all these public services used in other parts of the system
    public dataMigrationService: DataMigrationService,
    public injectedActionsFactoryService: InjectedActionsFactoryService,
    public notificationMessageService: NotificationMessageService,
    public reTriggerUserActivationService: RetriggerUserActivationService,
    public searchService: SearchService,
    public workflowUtilService: WorkflowUtilService,
    private _adaptableFilterService: AdaptiveFilterService,
    private _authService: AuthenticationService,
    private _bulkOperationService: BulkOperationService,
    private _changeDetector: ChangeDetectorRef,
    private _coreActionCreator: CoreActionCreatorService,
    private _dashboardService: DashboardService,
    private _dynamicApiService: DynamicApiService,
    private _emrIntegrationService: EmrIntegrationService,
    private _filterService: FilterService,
    private _logService: LogService,
    private _navigationConfigService: NavigationConfigService,
    private _renderer: Renderer2,
    private _routerManagerService: RouterManagerService,
    private _searchService: SearchService,
    private _store: Store<any>,
    private _tablelistDataHandlerService: TablelistDataHandlerService,
    private _tablelistHandlerService: TablelistHandlerService,
    private _tablelistStoreHandlerService: TablelistStoreHandlerService,
    private _userActionCreatorService: UserActionCreatorService,
    private _workflowService: WorkflowService,
    private _worklistService: WorklistService,
    private _exportWorklistDataService: ExportWorklistDataService,
    private _worklistExportDataPollingService: ExportWorklistDataPollingService,
    private _UserActionCreatorService: UserActionCreatorService,
    private _router: Router,
    private notesService: NotesService

  ) {
    this._filterService.isWorklistFilter = true;
    this._tablelistDataHandlerService.initState();
    this.currentUserId = _authService.user.info.id;
    this.duplicatePatientsManager = new WorklistDuplicatePatientsManager(this);
    this.hasTrackBy = !!this.trackByField;

    this._filterService.getCurrentSelectedFilterQueryFromStore()
      .pipe(
        takeUntil(this._destroyer$.asObservable()),
        filter(filters => !!filters && !!Object.keys(filters).length)
      )
      .subscribe(() => {
        this._filterApplied = true;
      });
  }

  private static actionCondition(self: WorklistActionsComponent, action: ITablelistAction, conditionName: string) {
    if (!action[conditionName]) {
      return true;
    }

    const conditions = JSON.parse(action[conditionName]).conditions;

    return conditions.every((condition) => {
        return ConditionalItem.builder(self.rowData, condition.value, condition.operator, condition.path).verify();
      }
    );
  }

  static updateCheckedState() {
    TableHeaderCheckbox.prototype.updateCheckedState = function () {
      const currentRows = _.map(this.dt.value, this.dt.dataKey);
      const selectedRows = _.map(this.dt.selection, this.dt.dataKey);
      const commonRows = _.intersection(currentRows, selectedRows);

      return commonRows.length === currentRows.length;
    };
  }

  static toggleRowsWithCheckbox() {
    Table.prototype.toggleRowsWithCheckbox = function (event, check) {
      let _selection: [];

      if (!check) {
        _selection = this.value.slice();
        _.each(_selection, (row) => {
          _.remove(this._selection, row);
        });
      } else {
        _selection = check ? this.filteredValue ? this.filteredValue.slice() : this.value.slice() : [];

        if (!this._selection) {
          this._selection = [];
        }

        // Concatenate and distinct duplicate items
        this._selection = Array.from(new Set(this._selection.concat(_selection)));
      }

      this.preventSelectionSetterPropagation = true;
      this.updateSelectionKeys();
      this.selectionChange.emit(this._selection);
      this.tableService.onSelectionChange();
      this.onHeaderCheckboxToggle.emit({
        originalEvent: event,
        affectedRows: _selection,
        checked: check
      });
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes.appliedFilterObj && !_.isEqual(changes.appliedFilterObj.previousValue, changes.appliedFilterObj.currentValue))
      && !changes.appliedFilterObj.firstChange
      && this._isConfigLoaded
    ) {
      if (this._appliedFilterObj) {
        this.resetFlags();

        if (this._searchText === '') {
          this.setWorklist(true);
        } else {
          this.filterResults(true);
        }
      }
    }

    if (
      (changes.resource && changes.resource.previousValue !== changes.resource.currentValue)
      && !changes.resource.firstChange
      && this._isConfigLoaded
    ) {
      this.resetFlags();
      this._tablelistDataHandlerService.clearRequestEmitter();
      this._lastSort = '';
      this.requesterConfig.configTemplateType = this._resource;
      this.requesterConfig.resourceType = this.getResourceType();

      if (!this.blockInitialLoad) {
        this.loadConfiguration(this.resource)
          .pipe(
            takeUntil(this._destroyer$.asObservable()),
          )
          .subscribe(() => {
          });
        this.emitDataLoad(true);
      }
    }

    if (this.getResourceType() === ResourceTypes.Manual && this._isConfigLoaded) {
      if (
        (changes.values && changes.values.previousValue !== changes.values.currentValue)
        || (changes.searchText && changes.searchText.previousValue !== changes.searchText.currentValue)
      ) {
        this.filterResults();
        this.length = this.values.length;

        const currentSortOrder = this._lastSort
          ? this._lastSort.split('<>')[1]
          : this.sortOrder;

        if (!(changes.values && changes.values.firstChange)) {
          // Setting current page on startup. In use, for example, when entering curation editor from second page and
          // getting back to curation list. Curation list should e opened in the second page.
          let firstItem: number = 0;

          if (this.lastEvent.page && !this.isInfiniteTotalCounting) {
            firstItem = this.lastEvent.page * this.lastEvent.rows;
            this.lastEvent.first = firstItem;
          }

          this.loadMoreRows(
            {
              rows: this.lastEvent.rows,
              first: firstItem,
              sortField: this.sortBy,
              sortOrder: currentSortOrder,
            },
            true,
            false,
            false
          ).then();
        }
      }
    }
  }

  private cleanPatientIfDefined() {
    //Clearing everything related to patient context when came back to  worklist if defined.
    let app = sessionStorage.getItem('currentApplication');
    app = app && JSON.parse(app);
    if (_.isNull(app) || (!!app && !app['retainPatientInWorklist'])) {
      this._userActionCreatorService.clearPatient();
    }
  }

  ngOnInit() {
    this.cleanPatientIfDefined();

    sessionStorage.removeItem(EXPORT_DATA_STORAGE_KEY);

    if (sessionStorage.getItem(this.stateKey)) {
      const storedObject = JSON.parse(sessionStorage.getItem(this.stateKey));
      storedObject.selection = [];
      sessionStorage.setItem(this.stateKey, JSON.stringify(storedObject));
    }

    window.onbeforeunload = () => this.ngOnDestroy();

    sessionStorage.setItem('worklistChanged', 'true');
    let shouldRecalculate = false;
    this._currentOffset = null;
    this.reloadWorklistTableConfig();
    this.loading = false;
    this._changeDetector.markForCheck();
    this._searchOperations
      .pipe(
        tap(() => {
          this.loading = true;
          this._changeDetector.markForCheck();
          this.reloadTable(true);
        }),
        takeUntil(this._destroyer$.asObservable()),
        filter(searchResult => !!searchResult),
        debounceTime(2000)
      )
      .subscribe(() => {
        if (moment(this._searchText, 'DD MMM YYYY', true).isValid()) {
          this._searchText = moment(this._searchText).format('YYYY[-]MM[-]DD');
        }
        if(shouldRecalculate) {
            this.filterResults(true);
          }
      });

    // The following handles the issue of clicking a worklist row in order to get into a test, and before the test is loaded and the worklist is destoyed,
    // the user clicks on the nav bar (e.g. on Worklist tab). Without this change there will be an infinite spinner.
    this._navigationConfigService.clickedNavItem$.pipe(
      takeUntil(this._destroyer$.asObservable()),
    ).subscribe(() => {
      if (this._inTestLoading) {
        this.loading = false;
        this._inTestLoading = false;
        this._changeDetector.markForCheck();
      }
    });

    this.loadConfigTemplate()
      .pipe(
        takeUntil(this._destroyer$.asObservable()),
        filter(() => this._loadedResourceId === null),
      )
      .subscribe((config) => {
        this._isConfigLoaded = true;
        this._loadedResourceId = config.id;

        if (this.localConfig.tableStateEnabled && !sessionStorage.getItem(this.stateKey)) {
          const tableState = {
            first: 0,
            rows: this._tableState.rows || 20,
            selection: [],
            sortOrder: +this.requesterConfig.sortByOrder,
            sortField: this.requesterConfig.sortByField
          };
          sessionStorage.setItem(this.stateKey, JSON.stringify(tableState));
          this._tableState = tableState;
          this.lastEvent.rows = this._tableState.rows;
        }

        this.setWorklist(true); // the first request
        if(!_.isEmpty(this._searchText)){
          shouldRecalculate = false;
        }
        setTimeout(() => {
          shouldRecalculate = true;
        }, 3000);
        //Since everytime the worklist is destroyed
        // if there is search keyword its repainting twice
        //this settime out will avoid it
      });

    // Register to bulk operations service (and its poller) only if there are bulk operations for the current worklist
    if (this.localConfig && this.localConfig.bulkOperations && Array.isArray(this.localConfig.bulkOperations.operations)
      && this.localConfig.bulkOperations.operations.length > 0) {
      this._bulkOperationService.register(this._worklistTemplateId);
      this._bulkOperationService.activeBulkOperations$.pipe(takeUntil(this._destroyer$.asObservable()))
        .pipe(takeUntil(this._destroyer$.asObservable()))
        .subscribe(data => {
          const responseStr = JSON.stringify(data); // Ignore similar messages

          if (this._lastBulkOperationResponse === responseStr) {
            return;
          }

          this._lastBulkOperationResponse = responseStr;

          if (data && data.length > 0) {
            let operationName: string = 'Unknown';

            if (data[0]['name'] === REASSIGN_BULK_OPERATION) {
              operationName = 'Reassign operation';
            }

            if (data[0].status) {
              if (data[0].status.value === BULK_OPERATION_STATUS.BULK_SUCCESS) {
                this.bulkOperationDisabled = false;
                this.notificationMessageService.success(operationName + ' completed successfully. You can refresh the worklist.');
              } else if (data[0].status.value === BULK_OPERATION_STATUS.BULK_PARTLY_SUCCESS) {
                this.bulkOperationDisabled = false;
                this.notificationMessageService.warning(operationName + ' completed with failures. You can refresh the worklist.');
              } else if (data[0].status.value === BULK_OPERATION_STATUS.BULK_FAILED) {
                this.bulkOperationDisabled = false;
                this.notificationMessageService.error(operationName + ' has failed.');
              } else {
                this.bulkOperationDisabled = true; // Bulk operations are disabled in case another bulk operation is running
              }

              if (!this.bulkOperationDisabled) {
                // In case operation bulk operation was completed, setting status as notified (and then the message will not be shown again)
                this._bulkOperationService.setBulkOperationNotified(data[0]);
              }
            } else {
              // Not supposed to get here
              this.bulkOperationDisabled = false;
            }
          } else {
            // Not supposed to get here
            this.bulkOperationDisabled = false;
          }
          this.bulkOperationDisabledStatus.emit(this.bulkOperationDisabled);
        });
    }

    this._worklistExportDataPollingService.register();
  }

  ngOnDestroy(): void {
    sessionStorage.removeItem(EXPORT_DATA_STORAGE_KEY);

    this._store.select('filters', 'currentSelected').pipe(take(1)).subscribe(data => {
      // @ts-ignore
      let isDefault;
      for (let value of data.values()) {
        isDefault = value.filter.default;
        break;
      }

      if (!isDefault && this._tableState.visitedRowId == null && !this._worklistService.actionUsed) {
        sessionStorage.removeItem(SessionStorageService.WORKLIST_SESSION_STORAGE_NAME);
      }
    });

    if(this._worklistService.actionUsed) {
      if (this.length - 1 === this.lastEvent.first) {
        //In the following calcuation 'first' can negative in case worklist is filtered via quick filter
        this._tableState.first = this.lastEvent.first - this.lastEvent.rows >= 0 ? this.lastEvent.first - this.lastEvent.rows : 0;
      }
      sessionStorage.setItem(this.stateKey, JSON.stringify(this._tableState));
    }

    this._renderer = null;
    this.duplicatePatientsManager = null;
    this.loading = false;
    this._bulkOperationService.unregister();
    this._tablelistStoreHandlerService.clearAll(this.getResourceType(), this.resource);
    this._worklistService.updateActiveRow(null);
    if (!!this.myTable) {
      this.myTable = null;
    }
    if (!!this.rowElements) {
      this.rowElements = null;
    }
    this._changeDetector.markForCheck();

    this._destroyer$.next();
    this._destroyer$.complete();





    this._changeDetector.detach();
  }

  goToPage(page: KeyboardEvent) {
    if (page.code === 'Minus' || page.code === 'NumpadSubtract') {
      page.preventDefault();
    }

    if (page.code === 'Enter' || page.code === 'NumpadEnter' || page.type === 'blur') {
      this.customPaginator.changePage(+(page.target as HTMLInputElement).value - 1);
    }
  }

  getBufferSize() {
    switch (this._mode && this._mode.toLowerCase()) {
      case ResourceTypes.Generic:
        return this.length;
      case ResourceTypes.Manual:
      default:
        return this.values.length;
    }
  }

  getSortColumn(item, sortKeys, i) {
    let column = null;

    if (item.sortable && Array.isArray(sortKeys)) {
      column = sortKeys[i];
    }

    return column;
  }

  rowTrackBy() {
    const self = this;

    return function (index, row) {
      let trackByValue = _.get(row, self._trackByField, index);

      // if there are secondary fields to compare by the row's data, concatenate them all to the trackByValue
      if (self._secondaryTrackByFields) {
        _.forEach(self._secondaryTrackByFields, (field) => {
          trackByValue += _.get(row, field, index);
        });
      }

      return trackByValue;
    };
  }

  rowSelected($event, rowData) {
    if (rowData.data) {
      if (this._unselectedRowsIds) {
        _.remove(this._unselectedRowsIds, (unselectedRowId) => {
          return unselectedRowId === rowData.data.id;
        });
      }

      const exportData: any = {};
      const selectedDataForExportStr: string = sessionStorage.getItem(EXPORT_DATA_STORAGE_KEY);
      let identifierName: string;

      if (selectedDataForExportStr) {
        const selectedDataForExport: any = JSON.parse(selectedDataForExportStr);

        if (this._currentFieldsExportDataConfig) {
          identifierName = this._currentFieldsExportDataConfig.resources[0].resourceIdentifier;

          this._currentFieldsExportDataConfig.resources.forEach((resource) => {
            this.addSelectedRowToExportData(
              identifierName,
              selectedDataForExport.currentFields.resources,
              rowData,
              resource.resourceType);
          });
        }

        if (this._allFieldsExportDataConfig) {
          identifierName = this._allFieldsExportDataConfig.resources[0].resourceIdentifier;

          this._allFieldsExportDataConfig.resources.forEach((resource) => {
            this.addSelectedRowToExportData(
              identifierName,
              selectedDataForExport.allFields.resources,
              rowData,
              resource.resourceType
            );
          });
        }
        selectedDataForExport.selectionCount++;
        selectedDataForExport.maxExportedItems = this.exportDataConfig.maxExportedItems;
        selectedDataForExport.exportMethodAsync = this.exportDataConfig.exportMethodAsync;
        selectedDataForExport.exportBasedOnActor = _.get(this.exportDataConfig,'exportBasedOnActor',true);
        selectedDataForExport.exportType = _.get(this.exportDataConfig,'exportType','EXCEL');
        selectedDataForExport.worklistAlias = _.get(this.exportDataConfig,'worklistAlias','');
        const state: string = JSON.stringify(selectedDataForExport);
        sessionStorage.setItem(EXPORT_DATA_STORAGE_KEY, state);
      } else {
        if (this.exportDataConfig) {
          exportData.worklistType = this.exportDataConfig['worklistType'];
          exportData.worklistAlias = this.exportDataConfig['worklistAlias'];
          exportData.performer = this._authService.user.id;
          exportData.worklistTemplateId = this._worklistTemplateId;

          if (this.exportDataConfig.hasOwnProperty('baseWorklistTemplateId')) {
            exportData.baseWorklistTemplateId = this.exportDataConfig['baseWorklistTemplateId'];
          }

          if (this.exportDataConfig.hasOwnProperty('definition')) {
            exportData.definition = {} = this.exportDataConfig.definition;
          }

          let templates: any = {};

          if (this.exportDataConfig.exportResources) {
            if (this._currentFieldsExportDataConfig) {
              exportData.currentFields = {};
              exportData.currentFields.resources = [];

              this._currentFieldsExportDataConfig.resources.forEach(resource => {
                const rowResource: any = this.addRowResourceToExportData(resource, rowData.data);
                exportData.currentFields.resources.push(rowResource);
              });

              templates = this.addTemplatesToExportData(this.exportDataConfig, 'CurrentFields');

              if (templates) {
                if (templates.hasOwnProperty('xHtmlExportTemplate')) {
                  exportData.currentFields.xHtmlExportTemplate = {};
                  exportData.currentFields.xHtmlExportTemplate = templates.xHtmlExportTemplate;
                }

                if (templates.hasOwnProperty('taskDispatchTemplateId')) {
                  exportData.currentFields.taskDispatchTemplateId = templates.taskDispatchTemplateId;
                }
              }
            }

            if (this._allFieldsExportDataConfig) {
              exportData.allFields = {};
              exportData.allFields.resources = [];

              this._allFieldsExportDataConfig.resources.forEach(resource => {
                const rowResource: any = this.addRowResourceToExportData(resource, rowData.data);
                exportData.allFields.resources.push(rowResource);
              });

              templates = this.addTemplatesToExportData(this.exportDataConfig, 'AllFields');

              if (templates) {
                if (templates.hasOwnProperty('xHtmlExportTemplate')) {
                  exportData.allFields.xHtmlExportTemplate = {};
                  exportData.allFields.xHtmlExportTemplate = templates.xHtmlExportTemplate;
                }

                if (templates.hasOwnProperty('taskDispatchTemplateId')) {
                  exportData.allFields.taskDispatchTemplateId = templates.taskDispatchTemplateId;
                }
              }
            }
            exportData.selectionCount = 1;
            exportData.maxExportedItems = this.exportDataConfig.maxExportedItems;
            exportData.exportMethodAsync = this.exportDataConfig.exportMethodAsync;
            exportData.exportBasedOnActor = _.get(this.exportDataConfig,'exportBasedOnActor',true);
            exportData.exportType = _.get(this.exportDataConfig,'exportType','EXCEL');
            exportData.worklistAlias = _.get(this.exportDataConfig,'worklistAlias','');
            const state: string = JSON.stringify(exportData);
            sessionStorage.setItem(EXPORT_DATA_STORAGE_KEY, state);
          }
        }
      }
      this._dynamicApiService.setWorklistSelectedRows(this.selectedRows);

      if (this.localConfig.type.toLowerCase() === WorklistHelper.VALUESET_EDITOR_TYPE && $event.type === 'checkbox') {
        this.rowClicked.emit(rowData);
      }

      if (!this._unselectedRowsIds || this._unselectedRowsIds.length === 0) {
        this.checkHeaderCheckBox();
      }
    }
  }

  rowUnselected($event, rowData) {
    let currentFieldsSelectedIds: boolean = true;
    let allFieldsSelectedIds: boolean = true;
    const storedDataForExportStr: string = sessionStorage.getItem(EXPORT_DATA_STORAGE_KEY);

    this._dynamicApiService.setWorklistSelectedRows(this.selectedRows);

    if (this.localConfig.type.toLowerCase() === WorklistHelper.VALUESET_EDITOR_TYPE && $event.type === 'checkbox') {
      this.rowClicked.emit(rowData);
    }

    if (this._unselectedRowsIds) {
      this._unselectedRowsIds.push(rowData.data.id);
    }

    this.checkHeaderCheckBox();

    if (storedDataForExportStr) {
      const storedDataForExport: any = JSON.parse(storedDataForExportStr);

      if (storedDataForExport) {
        if (storedDataForExport.currentFields) {
          this._currentFieldsExportDataConfig.resources.forEach(resourceConfig => {
            const storedResources = storedDataForExport.currentFields.resources;
            const resourceToUncheckIdx = storedResources.findIndex(resourceItem => {
              if (resourceItem && resourceItem.resourceIdentifier) {
                return resourceItem.resourceIdentifier === resourceConfig.resourceIdentifier;
              }
            });
            const identifierName = resourceConfig.resourceIdentifier;

            if (resourceToUncheckIdx !== -1) {
              if (!storedDataForExport.currentFields.resources[resourceToUncheckIdx].excludedIds) {
                storedDataForExport.currentFields.resources[resourceToUncheckIdx].excludedIds = [];
              }

              _.remove(storedDataForExport.currentFields.resources[resourceToUncheckIdx].combinedIds, function (combinedId) {
                return combinedId['id'] === rowData.data[identifierName];
              });
              storedDataForExport.currentFields.resources[resourceToUncheckIdx].excludedIds.push(rowData.data[identifierName]);

              if (storedDataForExport.currentFields.resources[resourceToUncheckIdx].combinedIds.length === 0) {
                currentFieldsSelectedIds = false;
              }
            }
          });
        } else {
          currentFieldsSelectedIds = false;
        }

        if (storedDataForExport.allFields) {
          this._allFieldsExportDataConfig.resources.forEach(resourceConfig => {
            const storedResources = storedDataForExport.allFields.resources;
            const resourceToUncheckIdx = storedResources.findIndex(resourceItem => {
              if (resourceItem && resourceItem.resourceIdentifier) {
                return resourceItem.resourceIdentifier === resourceConfig.resourceIdentifier;
              }
            });
            const identifierName = resourceConfig.resourceIdentifier;

            if (resourceToUncheckIdx !== -1) {
              if (!storedDataForExport.allFields.resources[resourceToUncheckIdx].excludedIds) {
                storedDataForExport.allFields.resources[resourceToUncheckIdx].excludedIds = [];
              }

              _.remove(storedDataForExport.allFields.resources[resourceToUncheckIdx].combinedIds, function (combinedId) {
                return combinedId['id'] === rowData.data[identifierName];
              });

              storedDataForExport.allFields.resources[resourceToUncheckIdx].excludedIds.push(rowData.data[identifierName]);

              if (storedDataForExport.allFields.resources[resourceToUncheckIdx].combinedIds.length === 0) {
                allFieldsSelectedIds = false;
              }
            }
          });
        } else {
          allFieldsSelectedIds = false;
        }

        if (!currentFieldsSelectedIds && !allFieldsSelectedIds && !storedDataForExport.query) {
          sessionStorage.removeItem(EXPORT_DATA_STORAGE_KEY);
        } else {
          if (!!storedDataForExport.selectionCount) {
            storedDataForExport.selectionCount--;
          }
          storedDataForExport.maxExportedItems = this.exportDataConfig.maxExportedItems;
          storedDataForExport.exportMethodAsync = this.exportDataConfig.exportMethodAsync;
          const state: string = JSON.stringify(storedDataForExport);
          sessionStorage.setItem(EXPORT_DATA_STORAGE_KEY, state);
        }
      }
    }
  }

  async selectAllRows(event) {
    this.setSelectedItem(event.checked);

    if (event.hasOwnProperty('checked')) {
      if (event.checked) {
        this._unselectedRowsIds = [];
        const totalRecords = await this._totalRecords$.pipe(take(1)).toPromise();

        if (this.loadedItemsAllPages.length < totalRecords) {
          if (this.exportDataConfig.hasOwnProperty('maxExportedItems')) {
            this._tablelistDataHandlerService.getQueryForExport(this.requesterConfig)
              .pipe(take(1))
              .subscribe(query => {
                this.prepareDataForExport(
                  this.loadedItemsAllPages,
                  null,
                  totalRecords,
                  this.loadedItemsAllPages.length === this.selectedRows.length,
                  query
                );
              });
          } else {
            sessionStorage.setItem(
              EXPORT_DATA_STORAGE_KEY,
              JSON.stringify({
                errorMessage: 'Missing configuration for max items to export.'
              })
            );
          }
        } else {
          this.prepareDataForExport(
            this.loadedItemsAllPages,
            null,
            this.selectedRows.length,
            this.loadedItemsAllPages.length === this.selectedRows.length
          );
        }
      } else {
        this._unselectedRowsIds = null;
        // unselecting all clears cache
        sessionStorage.removeItem(EXPORT_DATA_STORAGE_KEY);
      }
    }
  }

  rowClickedCallback($event, rowData) {
    this.setVisitedRow(rowData);

    if ($event.type !== 'checkbox') {
      this._store.dispatch({type: CURRENT_ORDER_ACTIONS.INIT_CURRENT_ORDER_STATE});

      if (rowData.data['DiagnosticOrder#Patient#otherKnownIds'] != null) {
        this._store.dispatch({
          type: USER_ACTIONS.SET_CURRENT_PATIENTLINK,
          payload: rowData.data['DiagnosticOrder#Patient#otherKnownIds']
        });
      } else {
        this._store.dispatch({type: USER_ACTIONS.SET_CURRENT_PATIENTLINK, payload: ['0']});
      }

      if (this.getResourceType() === ResourceTypes.Generic) {
        if (_.get(rowData, 'config.redirectUrl') !== '#') {
          if (this.localConfig.type === WORKLIST_TYPE_PATIENT_LIST) {
            this.duplicatePatientsManager.execute(rowData);

            return;
          }

          this.loading = true;
          this._changeDetector.markForCheck();
        }

        const redirectUrl: string = this._routerManagerService.getUrlFromConfig(
          rowData,
          rowData.config.redirectUrl,
          rowData.config.redirectUrls
        );
        this.goToState(redirectUrl, {rowData: rowData});
      } else {
        this.rowClicked.emit(rowData);
      }
    }
  }

  onSort(e) {
    // if this condition is a must for some reason this will have multi call on the moment of click
    if (!this._blockSortTrigger) {
      this._blockSortTrigger = true;
      // we are checking if the sort on allow field
      const items = this.localConfig.table;
      const sort = e.field;
      const searchEnabledSortItem = items.find(item => item.sortable && this.locatedSortKey(item.keys, sort));

      if (searchEnabledSortItem) {
        this.sortOrder = e.order;
        this.sortBy = this._tablelistDataHandlerService.mappingSortHotFix(sort);
        this._tableState.sortField = this.sortBy;
        this._tableState.sortOrder = this.sortOrder;
        this.requesterConfig.sortByOrder = this.sortOrder;
        this.requesterConfig.sortByField = this.sortBy;
        // for the nested objects path should be sent as comma separated list
        // this.requesterConfig.sortByField = this.sortBy.replace('.', ',');

        if (this.getResourceType() === ResourceTypes.Generic) {
          this.requesterConfig.resetIds = true;
          this.resetTableStateToFirstPage();
          this.emitDataLoad(true);
        } else {
          // if we ever need reset the page on sort when we on manual mode so uncomment the last 2 lines
          // this.lastEvent.first = 0;
          // this.lastEvent.page = 0;
          this.sortBy = e.field;
          this.sortOrder = e.order;
          this.loadMoreRows(this.lastEvent, true, false, true).then();
        }

        this.updateSelectedRowsOrder();
      }
    }
  }

  // event.first = Index of the first record (started from zer0)
  // event.rows = Number of rows to display in new page
  // event.page = Index of the new page (started from zer0)
  // event.pageCount = Total number of pages
  paginate(event) {
    if (this.lastEvent.rows != event.rows) {
      this.lastEvent = {...event};
      this.createNewBuffer();
    } else {
      this.lastEvent = {...event};
    }

    if (this._previousPage === event.page && this.lastEvent.rows === this._tableState.rows) {
      return;
    }

    // This is needed for the dynamic query but with this, the feature "select all" is not working properly
    // if (event.page === 0) {
    //   this.createNewBuffer();
    // }

    this._previousPage = event.page;
    this._allowUpdatesList = true;
    this.requesterConfig.start = event.first;
    this.requesterConfig.offset = this.requesterConfig.start + event.rows;
    this._checkIfRowExistsInNextPages = false;
    this._checkIfRowExistsInPreviousPages = false;
    this.changeChunkSizeForQuery(event.rows);
    this.notesService.revisionIdsReset();// Lets reset on page change
    this.loadMoreRows(this.lastEvent, true, true, false).then();
    this.pageChanged.emit(this.lastEvent.page + 1); // need + 1 because other components use +1!!!
    this.myTable.onPageChange(event);
    this._tableState = sessionStorage.getItem(this.stateKey)
      ? JSON.parse(sessionStorage.getItem(this.stateKey))
      : this._tableState;
  }

  updateNoteCompleted(row) {
    this.noteCompletedEmit.emit(row);
  }

  hasTableListItem(last) {
    if (this._hasActionColumn) {
      return !last;
    }

    return true;
  }

  hasTableListItemActions(last, rowData, idx) {
    if (!this._hasActionColumn) {
      return idx === (this.titles).length - 1 && (rowData.isActionEnabled === undefined || rowData.isActionEnabled);
    } else {
      return last && (rowData.isActionEnabled === undefined || rowData.isActionEnabled);
    }
  }

  titleTrackByFn(index, item: TableItemInterface) {
    return item.keys[0].key + index;
  }

  conditionApplied(data) {
    const condition = this.localConfig.condition;

    if (condition) {
      const value = _.get(data, condition.key);

      return value !== condition.value;
    } else {
      return true;
    }
  }

  onClickAutomationButton(): void {
    this._logService.fatal('TESTING: AUTOMATION-FATAL', 'WORKLISTCOMPONENT');
  }

  onClickAutomationIDB(): void {
    this._logService.error('TESTING: AUTOMATION-ERROR', 'WORKLISTCOMPONENT');
  }

  onClickAutomationWindowError(): void {
    throw new Error('Testing purpose');
  }

  displayWorklistHeaderExport(): boolean {
    let display = false;

    if (
      (this.mode.toLowerCase() === ResourceTypes.Generic && this.list && this.list.length > 0)
      || (this.mode.toLowerCase() === ResourceTypes.Manual && this.values && this.values.length > 0)
    ) {
      display = true;
    }

    return display;
  }

  /**
   * This method called from web/client/src/app/worklist/components/worklist/injected-actions/data-migration/validate-data-migration.ts
   *
   * @param worklist
   * @param fieldsToUpdate
   * @param status
   * @param meta
   * @param rowId
   */
  refreshWorklist(worklist, fieldsToUpdate: any, status: string, meta, rowId: string) {
    worklist.loading = true;

    // define a callback function that is invoked to update the given item, if is found
    const updaterCallback = function (item: any): any {
      if (item.hasOwnProperty('WorkflowRequest')) {
        item['WorkflowRequest']['revisions_-1_status'] = status;
      }

      item = {...item, ...fieldsToUpdate};

      if (!item['meta']) {
        item['meta'] = {};
      }

      item['meta']['versionId'] = meta.versionId;
      item['meta']['lastUpdated'] = meta.lastUpdated;

      return item;
    };

    // call the table-data-handler to extract the relevant row's data.
    // If found, updaterCallback is invoked to update the data.
    worklist._tablelistDataHandlerService.updateRowData(worklist.requesterConfig, rowId, updaterCallback)
      .pipe(
        filter(updatedItem => !!updatedItem),
        take(1)
      )
      .subscribe(() => {
        worklist.emitDataLoad(true);
      });
  }

  //click from providerPortal
  private goToState(redirectUrl, param = {}) {
    let EMRIntegrationObj = this.localConfig.type.includes(WorklistHelper.PATIENT_DASHBOARD_TEST) && this._emrIntegrationService ?
      this._emrIntegrationService.getEMRIntegrationObj() : null;

    const uriState = this._routerManagerService.getStateToGo(redirectUrl, param, EMRIntegrationObj);
    this._filterService.isWorklistFilter = false;
    this._inTestLoading = true;

    // Based on url pattern decide whether skipLocationChange is required or not
    const skipLocationChange = uriState && uriState.name && uriState.name.indexOf(WorklistHelper.PATHOLOGIST_ROUTE) > -1;
    const queryParamsHandlingValue = uriState && uriState.name && uriState.name.indexOf(WorklistHelper.ADMIN_VALUESET_EDITOR_TYPE) > -1 ? 'merge' : 'preserve'
    uriState && uriState.name ? this._router.navigate([uriState.name], {queryParams: uriState.queryParams,queryParamsHandling: queryParamsHandlingValue,skipLocationChange: skipLocationChange}) : this.loading = false;
    this._changeDetector.markForCheck();
  }

  private loadConfigTemplate(): Observable<ITablelistStateListItemConfig> {
    if (this.resource !== '') {
      this._tablelistDataHandlerService.clearRequestEmitter();
    }

    if (this._authService.getLocalSetting('worklistSearchText', '')) {
      this._searchText = this._authService.getLocalSetting('worklistSearchText', '');
      this._searchResource = this._authService.getLocalSetting('worklistSearchResource', '');
      this._searchOperations.next(this._searchText);
    }

    if (this._searchText && this.getResourceType() === ResourceTypes.Generic) {
      this.onSearchEnd.emit(false);
    }

    if (this._resource && this._searchResource !== this._resource && !this.searchPropagation) {
      this._searchText = '';
    }

    this._tablelistDataHandlerService.getConfigTemplate(this.resource).pipe(take(1)).subscribe();

    return this.loadConfiguration(this.resource);
  }

  private loadConfiguration(resource: string): Observable<ITablelistStateListItemConfig> {
    return this._tablelistStoreHandlerService.getConfigTemplateItem(resource)
      .pipe(
        filter((config: ITablelistStateListItemConfig) => !!config),
        take(1),
        switchMap((config: ITablelistStateListItemConfig) => {
          return this._dashboardService.getFacilityTableConfig(
            config.table,
            _.get(config, 'id'),
            _.get(config, 'extends', '')
          )
            .map((data: TableItemInterface[]) => {
              config.table = data;

              return config;
            });
        }),
        map((config: ITablelistStateListItemConfig) => {
          if (config.tableStateEnabled && sessionStorage.getItem(this.stateKey)) {
            this._tableState = JSON.parse(sessionStorage.getItem(this.stateKey));
          }

          this.localConfig = config;
          this.titles = config.table;

          // Add all fields keys and labels to the worklistColumns array
          if (this.titles) {
            this._filterService.currentWorklistColumns = this.titles.reduce((accum, column) => {
              const columns = (column.keys && column.keys.map(columnKey => ({
                key: columnKey.key,
                label: column.label
              })) || []);

              return [...accum, ...columns];
            }, []);
          }

          this.requesterConfig.dynamicSearch = this.updateDynamicSearchConfig(config).dynamicSearch;
          this.requesterConfig.associationItems = _.get(config, 'associationItems');
          this.requesterConfig.additionalResourcesToRetrieve = config.additionalResourcesToRetrieve;
          this.requesterConfig.adaptableFilters = _.get(config, 'adaptableFilters');
          this.requesterConfig.simpleSearch = config.queryType === SIMPLE_QUERY ? _.get(config, 'simpleSearch', null) : null;
          this.requesterConfig.queryType = config.queryType === SIMPLE_QUERY ? _.get(config, 'queryType') : null;
          this.requesterConfig.resource = config.resource;
          this.requesterConfig.resetIds = true;
          this.requesterConfig.sortByOrder = this._tableState.sortOrder ? this._tableState.sortOrder : config.sortByOrder;
          this.requesterConfig.sortByField = this._tableState.sortField ? this._tableState.sortField : config.sortByField;
          this.sortBy = this.requesterConfig.sortByField;
          // GEN-190267 Curation worklist should have the defined sort order, not to be undefined
          this.sortOrder = !!this.requesterConfig.sortByOrder ? this.requesterConfig.sortByOrder : this.sortOrder;

          this._tablelistStoreHandlerService
            .clear(this.requesterConfig.resourceType, this.requesterConfig.configTemplateType)
            .pipe(
              takeUntil(this._destroyer$.asObservable()),
            )
            .subscribe();
          this._mode = config.resourceTypeCode || ResourceTypes.Manual;
          this.worklistActions = _.get(config, 'actions.labels', {});
          this.disabledWorklistActions = _.get(config, 'actions.disbled', []);
          this._numberOfChunkSizeForSearch = config.numberOfChunkSizeForSearch || 1;
          this.localConfig.tableStateEnabled = config.tableStateEnabled || false;

          if (_.get(config, 'trackByField', null)) {
            this.trackByField = config.trackByField;

            // get if there are secondary fields by which to track row's data changes
            this._secondaryTrackByFields = null;
            const secondaryTrackFields = _.get(config, 'secondaryTrackByFields', null);

            if (secondaryTrackFields && secondaryTrackFields.length > 0) {
              this._secondaryTrackByFields = secondaryTrackFields;
            }
          } else {
            console.warn('No trackByField Property, data may not be updated as expected ');
            this.trackByField = this._originalTrackBy;
          }

          this.hasTrackBy = !!this.trackByField;
          this.lastEvent.rows = this._tableState.rows || config.maxItemsPerPage; // TODO Here add config from organisation per page, the config.maxItemsPerPage useless
          this._currentConfigTemplate = config; // duplication of this.localConfig

          this.changeChunkSizeForQuery(this.lastEvent.rows);

          let isExportDataOptionDisabled = false;
          let exportActivityInProgress = false;
          if (config.hasOwnProperty(EXPORT_DATA_STORAGE_KEY)) {
            this.exportDataConfig = config.exportData || {};
            this._worklistTemplateId = config.id;

            if (this.exportDataConfig.hasOwnProperty('exportResources')) {
              this._currentFieldsExportDataConfig = this.exportDataConfig.exportResources
                .find(exportResourceConfig => exportResourceConfig.type === 'currentFields');
              this._allFieldsExportDataConfig = this.exportDataConfig.exportResources
                .find(exportResourceConfig => exportResourceConfig.type === 'allFields');
              this.canExportData = true;
            }

            if (this.exportDataConfig.exportMethodAsync) {
              isExportDataOptionDisabled = true;
              exportActivityInProgress = true;
              const performer = _.get(this.exportDataConfig,'exportBasedOnActor',true)? this._authService.user.id: null;
              this._exportWorklistDataService.getExportDataActiveTasks(performer, this._worklistTemplateId)
                .pipe(take(1))
                .subscribe((activities) => {
                  if (activities && activities.length === 0){
                    exportActivityInProgress = false;
                    this._exportWorklistDataService.setExportWorklistDataOptionDisabledStatus({disabled:false,exportRunning:false}, this._worklistTemplateId);
                  }else{
                    this._worklistExportDataPollingService.requestPoll(false)
                  }
                });
            }
          }
          let activityExpirationTimeSec = _.get(this.exportDataConfig,'activityExpirationTimeSec',null);
          this._exportWorklistDataService.setCustomExpirationTime(activityExpirationTimeSec);
          this._exportWorklistDataService.setCurrentWorklistTemplate(this._worklistTemplateId);
          this._exportWorklistDataService.setExportWorklistDataOptionDisabledStatus({disabled:isExportDataOptionDisabled,exportRunning:exportActivityInProgress}, this._worklistTemplateId);

          this._searchableFields = new Map<string, IKeyElement>();
          this.sortKeys = config.table
            .map(title => this.getSortKey(title.keys))
            .map(keys => keys);
          this._hasActionColumn = (!!config.table.find((item: TableItemInterface) => item.hasOwnProperty('type') && item.type === 'actions'));

          if (!this._injectedActionsCreated
            && this.localConfig.actions
            && this.localConfig.actions['injected']
            && Array.isArray(this.localConfig.actions['injected'])) {
            this._injectedActionsCreated = true; // Sometimes 'loadConfiguration' is called twice on startup. Prevent actions creation twice.
            const configActions = Array.from(this.localConfig.actions['injected']);
            const bindObj = {window: self, worklist: this};

            configActions.forEach((action) => {
              this.injectedActions.push({
                action: this.popupForm.bind(bindObj),
                displayCondition: this.actionDisplayCondition.bind(self),
                displayConditionDefinition: action['displayCondition'],
                enableCondition: this.actionEnableCondition.bind(self),
                enableConditionDefinition: action['enableCondition'],
                key: action['key'],
                label: action['label'],
                payload: action['payload']
              });
            });
          }

          this.setCounterObservable();
          this.setWorklistActions();
          this.listenToData();

          return config;
        })
      );
  }

  private actionDisplayCondition(self: WorklistActionsComponent, action: ITablelistAction) {
    return WorklistComponent.actionCondition(self, action, 'displayConditionDefinition');
  }

  private actionEnableCondition(self: WorklistActionsComponent, action: ITablelistAction) {
    return WorklistComponent.actionCondition(self, action, 'enableConditionDefinition');
  }

  private resetFlags() {
    this.requesterConfig.preventLazyLoad = false;
    this.requesterConfig.resetIds = true;
    this.requesterConfig.sortByField = this._tableState.sortField ? this._tableState.sortField : null;
    this.requesterConfig.sortByOrder = this._tableState.sortOrder ? this._tableState.sortOrder : null;
    this.requesterConfig.searchTerm = this.searchText;
    this.requesterConfig.start = 0;
    this.requesterConfig.offset = 0;

    if (this._isConfigLoaded && this.localConfig.tableStateEnabled) {
      if (!this._tableState.visitedRowId && this._worklistService.actionUsed == false) {
        this._tableState.first = 0;
        sessionStorage.setItem(this.stateKey, JSON.stringify(this._tableState));
      } else {
        this._worklistService.actionUsed = false;
      }

    }

    if (this.getResourceType() === ResourceTypes.Generic) {
      this._buffer = [];
    }
  }

  private changeChunkSizeForQuery(chunkSize = 50) {
    if (this.requesterConfig.simpleSearch) {
      this.requesterConfig.simpleSearch.chunkSize = chunkSize * 3;
    }

    this._tablelistDataHandlerService.chunkSize = chunkSize * 3;
    this._tablelistHandlerService.resourceHandler(this.getResourceType(), this.requesterConfig).chunk = chunkSize * 3;
  }

  private getResourceType() {
    return (this._mode.toLowerCase() !== ResourceTypes.Generic) ? ResourceTypes.Manual : ResourceTypes.Generic;
  }

  private createNewBuffer() {
    this._buffer = new Array(this.length);
    this._buffer.fill(undefined);
    // this.reloadTriggered.emit(true);
  }

  private listenToData() {
    this._tablelistDataHandlerService.getDataListener()
      .pipe(
        takeUntil(this._destroyer$.asObservable()),
        tap((data) => { // catching server error (500)
          if (this._errorShown) {
            this.notificationMessageService.error('', 1); //Removing the previous notification error. In use when exiting a test and wrong filter appears for a few seconds and then the correct filter appears.
            this._errorShown = false;
          }
          if (data['status'] && data['status'] === 500) {
            this.notificationMessageService.error('Error loading worklist. Please refresh the page.');
            this._errorShown = true;
          }
          this.loading = false;
          this.initialized = true;
          this._changeDetector.detectChanges();
        })
      )
      .subscribe((data: ITablelistStateListItem) => {
        this._blockSortTrigger = false;

        if (this.getResourceType() === ResourceTypes.Generic) {
          if (typeof data.data === 'undefined') {
            return;
          }

          this._totalRecords$
            .pipe(
              take(2)
            )
            .subscribe((amount) => {
              this.length = amount;

              if (this._buffer.length !== this.length) {
                this.createNewBuffer();
              }

              this._buffer.splice(Math.floor(this.lastEvent.page / 3) * this.lastEvent.rows * 3, data.data.length, ...data.data);

              this.loadedItemsAllPages = [...this._buffer].filter(item => !!item);

              if (this.requesterConfig.resetIds) {
                this.requesterConfig.ids = data.ids || [];
              }

              this._allowUpdatesList = true;

              if (!this._checkIfRowExistsInNextPages && !this._checkIfRowExistsInPreviousPages || this._checkIfRowExistsInNextPages && this._checkIfRowExistsInPreviousPages) {
                this.loadMoreRows(this.lastEvent, true).then(); // TODO Here the second call to the data
              }

              this.scrollToVisitedRow(this.length);
            });
        }
      });
  }

  private highlightRowElement(row: ElementRef) {
    row.nativeElement.scrollIntoView({behavior: 'smooth', inline: 'start', block: 'start'});
    row.nativeElement.style.backgroundColor = '#E4EDF1';

    setTimeout(function () {
      row.nativeElement.style.backgroundColor = '';
    }, (12000));
  }

  private navigateToPage(first: number) {
    this.lastEvent.first = first;
    this.lastEvent.page = Math.floor(this.lastEvent.first / this.lastEvent.rows);
    this.currentPage = this.lastEvent.page + 1;
    this._checkIfRowExistsInNextPages = false;
    this._checkIfRowExistsInPreviousPages = false;
  }

  private scrollToVisitedRow(total: number) {
    if (this._tableState.visitedRowId) {
      let row = this.rowElements?.find(r => r.nativeElement.getAttribute('id') === this._tableState?.visitedRowId);

      if (row) {
        this.highlightRowElement(row);
      } else {
        const indexOfRow = this._buffer.findIndex(item => item && item.id === this._tableState?.visitedRowId);

        if (indexOfRow > -1) {
          if (this._navigationConfigService.getCurrentApplicationMemoryType()) {
            if (indexOfRow > this.lastEvent.first + this.lastEvent.rows || indexOfRow < this.lastEvent.first) {
              this.loadMoreRows(this.lastEvent, true).then();
              row = this.rowElements.find(r => r.nativeElement.getAttribute('id') === this._tableState.visitedRowId);
              this.highlightRowElement(row);
            } else {
              this.loadMoreRows(this.lastEvent, true).then();
            }
          } else {
            this.navigateToPage(Math.floor(indexOfRow / this.lastEvent.rows) * this.lastEvent.rows);
            this.loadMoreRows(this.lastEvent, true).then();
            row = this.rowElements.find(r => r.nativeElement.getAttribute('id') === this._tableState.visitedRowId);
            this.highlightRowElement(row);
          }
        } else {
          if (this._numberOfChunkSizeForSearch > 0 && !this._navigationConfigService.getCurrentApplicationMemoryType()) {
            if (!this._checkIfRowExistsInPreviousPages) {
              if (this.lastEvent.first - this._numberOfChunkSizeForSearch * this.lastEvent.rows >= 0) {
                this.requesterConfig.start = this.lastEvent.first - this._numberOfChunkSizeForSearch * this.lastEvent.rows;
                this.requesterConfig.offset = this.requesterConfig.start + this.lastEvent.rows;
                this.lastEvent.page = this.requesterConfig.start / this.lastEvent.rows;
                this.emitDataLoad(true, true);
              }
              this._checkIfRowExistsInPreviousPages = true;

              return;
            }

            if (!this._checkIfRowExistsInNextPages) {
              if (this.lastEvent.first + this._numberOfChunkSizeForSearch * this.lastEvent.rows <= total) {
                this.requesterConfig.start = this.lastEvent.first + this._numberOfChunkSizeForSearch * this.lastEvent.rows;
                this.requesterConfig.offset = this.requesterConfig.start + this.lastEvent.rows;
                this.lastEvent.page = this.requesterConfig.start / this.lastEvent.rows;
                this.emitDataLoad(true, true);
              }

              this._checkIfRowExistsInNextPages = true;

              return;
            }

            if (this._checkIfRowExistsInPreviousPages && this._checkIfRowExistsInNextPages) {
              this.navigateToPage(0);
              this.loadMoreRows(this.lastEvent, true).then();
            }
          }
        }
      }

      this._tableState.visitedRowId = null;

      if (this._isConfigLoaded && this.localConfig.tableStateEnabled) {
        sessionStorage.setItem(this.stateKey, JSON.stringify(this._tableState));
      }
    }
  }

  private setWorklist(forceEmit: boolean = false, resetSelectedRows: boolean = true) {
    if (this.resource !== '') {
      this.requesterConfig.resourceType = this.getResourceType();
      this.requesterConfig.preventLazyLoad = (this.getResourceType() === ResourceTypes.Generic);
      this.requesterConfig.configTemplateType = this.resource;
      this.requesterConfig.start = this._tableState.first;
      this.requesterConfig.offset = this.requesterConfig.start + this._tableState.rows;
      this.lastEvent.first = this._tableState.first;
      this.lastEvent.page = this.requesterConfig.start / this._tableState.rows;
      this.lastEvent.rows = this._tableState.rows;

      if (typeof this.requesterConfig.sortByOrder === 'string') {
        this.requesterConfig.sortByOrder = parseInt(this.requesterConfig.sortByOrder);
      }

      this.sortOrder = this.requesterConfig.sortByOrder ? +this.requesterConfig.sortByOrder : this.sortOrder;
      this.sortBy = this.requesterConfig.sortByField;
      this.currentPage = this.lastEvent.page + 1;

      this.setPractitionerType();

      if (resetSelectedRows) {
        this.selectedRows = [...this._worklistService.preserveSelectedRows];
        sessionStorage.removeItem(EXPORT_DATA_STORAGE_KEY);
        this.checkHeaderCheckBox();
        this._worklistService.preserveSelectedRows = [];
        this.prepareDataForExport(this.selectedRows, null, this.selectedRows.length, true);
      }

      if (this.localConfig && !_.get(this.localConfig, 'useLoadDebounce', true)) {
        this.emitDataLoad(forceEmit);
      } else {
        this._debounceEmitDataLoad(forceEmit);
      }
    }
  }

  /**
   * setting the actions of the worklist based on the configuration, doing once while the component loaded
   */
  private setWorklistActions() {
    const actionsConfig = this._currentConfigTemplate.actions;
    const labels = actionsConfig.labels || {};
    const enabledArray = actionsConfig.enabled && Array.isArray(actionsConfig.enabled) ? actionsConfig.enabled : [];
    const disabledArray = actionsConfig.disabled && Array.isArray(actionsConfig.disabled) ? actionsConfig.disabled : [];

    this.actions = [];
    this.actions = WorklistActionsComponent.ACTIONS;

    const ACTIONS = [...this.actions];
    ACTIONS.push.apply(ACTIONS, this.injectedActions);

    WorklistActionsComponent.ACTIVE_ACTIONS = ACTIONS.reduce((filteredArray, action) => {
      if (disabledArray.indexOf(action.key) === -1) {
        action = {...action};
        action.label = labels[action.key] || action.label;
        filteredArray.push(action);
      }

      return filteredArray;
    }, []);

    WorklistActionsComponent.ACTIVE_ACTIONS = WorklistActionsComponent.ACTIVE_ACTIONS.concat(
      WorklistActionsComponent.DISABLED_ACTIONS.reduce((filteredArray, action) => {
        if (enabledArray.indexOf(action.key) !== -1) {
          action = {...action};
          action.label = labels[action.key] || action.label;
          filteredArray.push(action);
        }

        return filteredArray;
      }, [])
    );

    this.actions = WorklistActionsComponent.ACTIVE_ACTIONS;
  }

  private getSortKey(keys) {
    let primaryKey = null;
    const elemWithMainKey = keys.find(elem => _.get(elem, 'isMainKey', false));

    if (!!elemWithMainKey) {
      if (this._keyTypes.indexOf(elemWithMainKey.type.toLowerCase()) > -1) {
        primaryKey = this._tablelistDataHandlerService.mappingSortHotFix(elemWithMainKey.key);
        this._sortTypes[elemWithMainKey.key] = elemWithMainKey.type;
      }

      this.setSearchableField(elemWithMainKey);
    } else {
      for (const elem of keys) {
        if (this._keyTypes.indexOf(elem.type.toLowerCase()) > -1 && !primaryKey) {
          primaryKey = this._tablelistDataHandlerService.mappingSortHotFix(elem.key);
          this._sortTypes[elem.key] = elem.type;
        } else if (!primaryKey) {
          continue;
        }

        this.setSearchableField(elem);
      }
    }

    return primaryKey;
  }

  private setSearchableField(element: IKeyElement) {
    if (this._keyTypes.indexOf(element.type.toLowerCase()) > -1) {
      this._searchableFields.set(element.key, element);
    }
  }

  private checkHeaderCheckBox() {
    // workaround: p-table doesn't redraw the table header checkbox (select/deselect all), so we do!!
    this.visible = false;

    const tmr = setTimeout(() => {
      this.visible = true;
      this._changeDetector.detectChanges();
      clearTimeout(tmr);
    }, 0);
  }

  private prepareDataForExport(worklistItems, cachedItems, totalRecords, restoreRows: boolean = false, query = null) {
    if (worklistItems && worklistItems.length > 0) {
      // let totalSelectedRows = 0;

      if (this.exportDataConfig.hasOwnProperty('maxExportedItems')) {
        const storedDataForExport = this.buildExportData(worklistItems, cachedItems, restoreRows);

        if (storedDataForExport) {
          if (query) {
            storedDataForExport.query = query;
          }

          storedDataForExport.selectionCount = totalRecords;
          storedDataForExport.maxExportedItems = this.exportDataConfig.maxExportedItems;
          storedDataForExport.exportMethodAsync = this.exportDataConfig.exportMethodAsync;
          storedDataForExport.exportBasedOnActor = _.get(this.exportDataConfig,'exportBasedOnActor',true);
          storedDataForExport.exportType = _.get(this.exportDataConfig,'exportType','EXCEL');
          storedDataForExport.worklistAlias =_.get(this.exportDataConfig,'worklistAlias','');
          sessionStorage.setItem(EXPORT_DATA_STORAGE_KEY, JSON.stringify(storedDataForExport));
        } else {
          this.notificationMessageService.error('Unable to build the exported data.');
        }
      } else {
        this.notificationMessageService.error('Missing configuration for max items to export.');
      }
    }
  }

  private buildExportData(rows: any[], cachedItems: any, restoreRows: boolean): any {
    let exportData: any = cachedItems;
    let templates: any;

    if (!exportData) {
      exportData = {};
      exportData.worklistType = this.exportDataConfig['worklistType'];
      exportData.worklistAlias = this.exportDataConfig['worklistAlias'];
      exportData.performer = this._authService.user.id;
      exportData.worklistTemplateId = this._worklistTemplateId;

      if (this.exportDataConfig.hasOwnProperty('baseWorklistTemplateId')) {
        exportData.baseWorklistTemplateId = this.exportDataConfig['baseWorklistTemplateId'];
      }

      if (this.exportDataConfig.hasOwnProperty('definition')) {
        exportData.definition = {} = this.exportDataConfig.definition;
      }

      const resourceDataForExport: any = {};
      resourceDataForExport.combinedIds = [];

      if (this._currentFieldsExportDataConfig) {
        exportData.currentFields = {};
        exportData.currentFields.resources = [];

        templates = this.addTemplatesToExportData(this.exportDataConfig, 'CurrentFields');

        if (templates) {
          if (templates.hasOwnProperty('xHtmlExportTemplate')) {
            exportData.currentFields.xHtmlExportTemplate = {};
            exportData.currentFields.xHtmlExportTemplate = templates.xHtmlExportTemplate;
          }

          if (templates.hasOwnProperty('taskDispatchTemplateId')) {
            exportData.currentFields.taskDispatchTemplateId = templates.taskDispatchTemplateId;
          }
        }
      }

      if (this._allFieldsExportDataConfig) {
        exportData.allFields = {};
        exportData.allFields.resources = [];

        templates = this.addTemplatesToExportData(this.exportDataConfig, 'AllFields');

        if (templates) {
          if (templates.hasOwnProperty('xHtmlExportTemplate')) {
            exportData.allFields.xHtmlExportTemplate = {};
            exportData.allFields.xHtmlExportTemplate = templates.xHtmlExportTemplate;
          }

          if (templates.hasOwnProperty('taskDispatchTemplateId')) {
            exportData.allFields.taskDispatchTemplateId = templates.taskDispatchTemplateId;
          }
        }
      }
    }

    if (this._currentFieldsExportDataConfig) {
      this._currentFieldsExportDataConfig.resources.forEach(exportResource => {
        this.addResourceExportData(rows, exportResource, exportData, restoreRows);
      });
    }

    if (this._allFieldsExportDataConfig) {
      this._allFieldsExportDataConfig.resources.forEach(exportResource => {
        this.addResourceExportData(rows, exportResource, exportData, restoreRows);
      });
    }

    return exportData;
  }

  /** exportSelectionOption: CurrentFields / AllFields */
  private addTemplatesToExportData(config: any, exportSelectionOption: string): any {
    const templates: any = {};

    if (config.hasOwnProperty('taskDispatchTemplates')) {
      templates.taskDispatchTemplateId = this.getTaskDispatchTemplateId(exportSelectionOption);
    }

    if (config.hasOwnProperty('xHtmlReportTemplates')) {
      templates.xHtmlExportTemplate = {} = this.getXhtmlReportTemplate(exportSelectionOption);
    }

    return templates;
  }

  private addResourceExportData(rows: any[], exportResource: any, exportData: any, restoreRows: boolean = false) {
    let idx: number;
    let selectedRowInCachedItemsIdx;

    rows.forEach((rowData, ind) => {
      /**
       * Check if the selected row id already exists in the cached selected rows.
       * If yes - ignore it, else - add it to the list of selected ids.
       */
      selectedRowInCachedItemsIdx = -1;

      if (exportData && rowData) {
        idx = restoreRows
          ? ind
          : this.loadedItemsAllPages.findIndex(value => value && value[exportResource.resourceIdentifier] === rowData[exportResource.resourceIdentifier]);

        if (exportData.hasOwnProperty('currentFields') && exportData.currentFields.hasOwnProperty('resources')) {
          if (exportData.currentFields.resources.length > 0) {
            selectedRowInCachedItemsIdx = exportData.currentFields.resources[0].combinedIds
              .findIndex(combinedId => combinedId.id === rowData[exportResource.resourceIdentifier]);
          } else {
            const newResource = this.createBaseResourceForExport(exportResource);
            exportData.currentFields.resources.push(newResource);
          }

          if (selectedRowInCachedItemsIdx === -1) {
            /** The selected row does not exist in cache. */
            if (idx !== -1) {
              exportData.currentFields.resources[0].combinedIds.push({
                id: rowData[exportResource.resourceIdentifier],
                idx: idx
              });
            }
          }
        }

        selectedRowInCachedItemsIdx = -1;

        if (exportData.hasOwnProperty('allFields') && exportData.allFields.hasOwnProperty('resources')) {
          if (exportData.allFields.resources.length > 0) {
            selectedRowInCachedItemsIdx = exportData.allFields.resources[0].combinedIds
              .findIndex(combinedId => combinedId.id === rowData[exportResource.resourceIdentifier]);
          } else {
            const newResource = this.createBaseResourceForExport(exportResource);
            exportData.allFields.resources.push(newResource);
          }

          if (selectedRowInCachedItemsIdx === -1) {
            /** The selected row does not exist in cache. */
            if (idx !== -1) {
              exportData.allFields.resources[0].combinedIds.push({
                id: rowData[exportResource.resourceIdentifier],
                idx: idx
              });
            }
          }
        }
      }
    });
  }

  private checkAndAlignTable() {
    try {
      if (this.myTable) {
        const element = this.myTable.el.nativeElement;
        const bodyDiv = element.querySelector('div.p-datatable-scrollable-body');
        const headerDiv = element.querySelector('div.p-datatable-scrollable-header');
        const hasScroll = bodyDiv.scrollHeight > bodyDiv.clientHeight;

        if (hasScroll) {
          this._renderer?.addClass(headerDiv, 'has-scroll');
        } else {
          this._renderer?.removeClass(headerDiv, 'has-scroll');
        }
      }
    } catch (e) {
      console.error(e);
    }
  }

  private createBaseResourceForExport(exportResource: any): any {
    return {
      combinedIds: [],
      resourceType: exportResource.resourceType,
      queryParam: exportResource.queryParam,
      resourceIdentifier: exportResource.resourceIdentifier
    };
  }

  private getTaskDispatchTemplateId(exportFieldsType: string): string {
    let taskDispatchTemplateId: string = '';

    if (this.exportDataConfig.taskDispatchTemplates && this.exportDataConfig.taskDispatchTemplates.length > 0) {
      const taskDispatchTemplate = this.exportDataConfig.taskDispatchTemplates
        .filter(template => template.type === exportFieldsType);

      if (taskDispatchTemplate && taskDispatchTemplate.length > 0) {
        taskDispatchTemplateId = taskDispatchTemplate[0].reference;
      }
    }

    return taskDispatchTemplateId;
  }

  private getXhtmlReportTemplate(exportFieldsType: string): any {
    let xHtmlReportTemplate: any = {};

    if (this.exportDataConfig.xHtmlReportTemplates && this.exportDataConfig.xHtmlReportTemplates.length > 0) {
      const xHtmlReportTemplatesConf = this.exportDataConfig.xHtmlReportTemplates
        .filter(template => template.type === exportFieldsType);

      if (xHtmlReportTemplatesConf && xHtmlReportTemplatesConf.length > 0) {
        xHtmlReportTemplate = {
          id: xHtmlReportTemplatesConf[0].reference,
          labels: xHtmlReportTemplatesConf[0].labels.templateLabels
        };
      }
    }

    return xHtmlReportTemplate;
  }

  private addRowResourceToExportData(resource: any, rowData: any): any {
    const currentResourceToStore: any = {
      combinedIds: [],
      resourceType: resource.resourceType,
      queryParam: resource.queryParam,
      resourceIdentifier: resource.resourceIdentifier
    };

    const idx = this.loadedItemsAllPages.findIndex(value => value && value[resource.resourceIdentifier] === rowData[resource.resourceIdentifier]);

    if (idx !== -1) {
      currentResourceToStore.combinedIds.push({id: rowData[resource.resourceIdentifier], idx: idx});
    } else {
      this.notificationMessageService.error('Selected row id is missing in filtered values.');
    }

    return currentResourceToStore;
  }

  private addSelectedRowToExportData(identifierName: string, selectedDataForExport: any[], rowData: any, resourceType: string) {
    const selectedDataToUpdateIdx = selectedDataForExport.findIndex(selectedResource => {
      if (selectedResource.resourceType === resourceType) {
        return selectedResource;
      }
    });
    let idx = -1;

    if (selectedDataToUpdateIdx !== -1) {
      idx = this.loadedItemsAllPages.findIndex(value => value && value[identifierName] === rowData.data[identifierName]);
      const isCombinedIdNotExists = selectedDataForExport[selectedDataToUpdateIdx].combinedIds.findIndex((cid) => {
        return cid.id === rowData.data[identifierName];
      });

      if (isCombinedIdNotExists === -1) {
        selectedDataForExport[selectedDataToUpdateIdx].combinedIds.push({id: rowData.data[identifierName], idx: idx});
        _.remove(selectedDataForExport[selectedDataToUpdateIdx].excludedIds, function (id) {
          return id['id'] === rowData.data[identifierName];
        });
        selectedDataForExport[selectedDataToUpdateIdx].combinedIds = selectedDataForExport[selectedDataToUpdateIdx].combinedIds
          .sort((a, b) => a.idx > b.idx ? 1 : a.idx < b.idx ? -1 : 0);
      }
    }
  }

  private setVisitedRow(rowData) {
    this._worklistService.preserveSelectedRows = [...this.selectedRows];
    this._tableState.visitedRowId = rowData.data.id;

    if (this._isConfigLoaded && this.localConfig.tableStateEnabled) {
      sessionStorage.setItem(this.stateKey, JSON.stringify(this._tableState));
    }
  }

  private locatedSortKey(keys, searchValue) {
    return !!keys.find((key) =>
      this._tablelistDataHandlerService.mappingSortHotFix(key.key) === this._tablelistDataHandlerService.mappingSortHotFix(searchValue)
    );
  }

  private resetTableStateToFirstPage() {
    this.requesterConfig.start = 0;
    this.lastEvent.first = 0;
    this._tableState.first = this.lastEvent.first;
    this.customPaginator.changePage(0);
    this._buffer = [];
    this._loadedItemsAllPages = [];

    if (this._isConfigLoaded && this.localConfig.tableStateEnabled) {
      sessionStorage.setItem(this.stateKey, JSON.stringify(this._tableState));
    }
  }

  private async loadMoreRows(event: LazyLoadEvent, forceEmit: boolean = false, paginate: boolean = false, fromSort: boolean = false) {
    this.lastEvent.page = event.first / this.lastEvent.rows;
    this.currentPage = this.lastEvent.page + 1;

    if (this.length > 0) {
      this._allowUpdatesList = true;

      if (this.getResourceType() === ResourceTypes.Generic) {
        if (forceEmit && this._allowUpdatesList) {
          const pageRange = event.first + event.rows;
          const notEmptyItemsCount = this._buffer.filter(item => !!item);

          if (this._buffer[this.requesterConfig.start] === undefined && notEmptyItemsCount.length < this.length) {
            this.emitDataLoad(forceEmit, paginate);
          }

          // indicate to resetIds if requesting first page OR if number of requested rows exceeds the
          //  number of totally retrieved IDs
          if (_.get(this.requesterConfig, 'dynamicSearch.clubbed')) {
            this.requesterConfig.resetIds = this.requesterConfig.start === 0 || pageRange > this.loadedItemsAllPages.length;
          } else {
            this.requesterConfig.resetIds = this.requesterConfig.start === 0 || pageRange > this.requesterConfig.ids.length;
          }

          this.list = [];
          this._allowUpdatesList = false;

          const temp = [...this._tablelistStoreHandlerService.getDataList(this._buffer, event.first, event.first + event.rows)];

          if (temp[0] !== undefined) {
            this.list = temp;
          }

          if (this.list.length > 0) {
            this.updateTitles();
            this.reloadTable(true);
            this.checkAndAlignTable();

            // init tableState for the first time
            if (!sessionStorage.getItem(this.stateKey) && this.myTable) {
              this.myTable.onPageChange(event);
            }
          }
        }
      } else {
        if (forceEmit || (this.values && '<>' !== this.sortBy + '<>' + this.sortOrder && this._lastSort !== this.sortBy + '<>' + this.sortOrder)) {
          const needSort = (this.getUseMultiSearch() && (!this.searchText || this.searchText.length === 0)) || (!this.getUseMultiSearch() || fromSort);
          if (needSort)
            this.values.sort((a: any, b: any) =>
              this._tablelistHandlerService.sort(a, b, this._sortTypes[this.sortBy], this.sortBy, this.sortOrder),
            );
          this._lastSort = this.sortBy + '<>' + this.sortOrder;
        }

        if (forceEmit || (this.values && this.values.length > 0)) {
          this.list = this.values.slice(event.first, event.first + this.lastEvent.rows);
        } else if (!(this.values && this.values.length > 0)) {
          this.list = [];
        } else {
          this.list = [...this.list];
        }

        this.setCounterObservable();
        let enabledDetectChanges = false;

        if (paginate && this.selectedRows && this.selectedRows.length > 0) {
          enabledDetectChanges = true;
        }

        this.reloadTable(enabledDetectChanges);
        this.loadedItemsAllPages = [...this.values];
      }

      this._blockSortTrigger = false;
    } else {
      this.list = [];
      this.reloadTable(true);
    }

    this.pageChanged.emit(this.currentPage);
  }

  private reloadTable(enabledDetectChanges = true) {
    if (!(this._changeDetector as ViewRef).destroyed) {
      this._changeDetector.markForCheck();
    }


    if (enabledDetectChanges && !(this._changeDetector as ViewRef).destroyed) {
      this._changeDetector.detectChanges();
    }
  }

  private setPractitionerType() {
    if (this._authService.user) {
      switch (this._authService.user.role.toLowerCase()) {
        case 'oncologist':
          this.practitionerType = 'orderer';
          break;
        default:
          this.practitionerType = 'performer';
          break;
      }
    }
  }

  private filterResults(forceEmit: boolean = false) {
    const useMultipleSearch = this.getUseMultiSearch();

    if (this.getResourceType() === ResourceTypes.Generic) {

      if (useMultipleSearch) {
        this.searchText = StringUtils.getNormalizedString(this.searchText);
      }

      this.requesterConfig.searchTerm = this.searchText;
      this.requesterConfig.start = 0;
      this.requesterConfig.offset = 0;

      if (this.searchText) {
        this.requesterConfig.resetIds = true;
      }

      // search has started, toggle on loader
      this.onSearchEnd.emit(false);
      this._debounceEmitDataLoad(forceEmit);
    } else {
      // search has started, toggle on loader
      this.onSearchEnd.emit(false);
      this.emitDataLoad(forceEmit);
      this.onSearchEnd.emit(true);

      if (this._searchText && this._values) {
        this._filteredValues = this._worklistService.filterByText(
          this.searchText,
          this._values,
          Array.from(this._searchableFields.values()),
          useMultipleSearch,
          this.getUseExactMultiSearch()
        );
      } else {
        this._filteredValues = this._values; // aliasing
      }
    }
  }

  private updateSelectedRowsOrder() {
    let cachedSelectedRowsStr: string = sessionStorage.getItem(EXPORT_DATA_STORAGE_KEY);

    if (cachedSelectedRowsStr && cachedSelectedRowsStr !== '') {
      const cachedSelectedRows = JSON.parse(cachedSelectedRowsStr);
      let selectedOrderedIds: any[] = [];
      let identifierName: string;

      if (cachedSelectedRows.hasOwnProperty('currentFields')) {
        identifierName = cachedSelectedRows.currentFields.resources[0].resourceIdentifier;
        selectedOrderedIds = cachedSelectedRows.currentFields.resources[0].orderedIds;
        cachedSelectedRows.currentFields.resources[0].orderedIds = this.orderSelectedCombinedIds(identifierName, selectedOrderedIds);
      }

      if (cachedSelectedRows.hasOwnProperty('allFields')) {
        identifierName = cachedSelectedRows.allFields.resources[0].resourceIdentifier;
        selectedOrderedIds = cachedSelectedRows.allFields.resources[0].orderedIds;
        cachedSelectedRows.allFields.resources[0].orderedIds = this.orderSelectedCombinedIds(identifierName, selectedOrderedIds);
      }

      cachedSelectedRowsStr = JSON.stringify(cachedSelectedRows);
      sessionStorage.setItem(EXPORT_DATA_STORAGE_KEY, cachedSelectedRowsStr);
    }
  }

  private orderSelectedCombinedIds(identifierName: string, selectedCombinedIds: any[]): any[] {
    if (selectedCombinedIds && selectedCombinedIds.length > 0) {
      selectedCombinedIds.forEach(combinedId => {
        const idx = this._buffer.findIndex(value => value[identifierName] === combinedId.id);

        if (idx !== -1) {
          combinedId.idx = idx;
        }
      });

      selectedCombinedIds = selectedCombinedIds.sort((a, b) => a.idx > b.idx ? 1 : a.idx < b.idx ? -1 : 0);
    }

    return selectedCombinedIds;
  }

  private setCounterObservable() {
    switch (this.mode.toLowerCase()) {
      case ResourceTypes.Generic:
        this._totalRecords$ = this._tablelistStoreHandlerService
          .getResourceRecordsAmount(this._currentConfigTemplate['type'])
          .pipe(
            filter((amount) => amount !== undefined),
            filter((amount) => amount !== null)
          );
        break;
      case ResourceTypes.Manual:
      default:
        this._totalRecords$ = of(this.length);
        break;
    }
  }

  private emitDataLoad(forceEmit: boolean = false, paginate: boolean = false) {
    this.requesterConfig.searchTerm = this.searchText;
    this.requesterConfig.queryOrigin = !!this.searchText ? SimpleSearchOrigin.SEARCHBOX : SimpleSearchOrigin.WORKLIST;
    this.requesterConfig.paginate = paginate; // TODO do we need this?

    if (!(this.requesterConfig.resetIds && this.requesterConfig.start === 0)) {
      this.requesterConfig['items'] = [...this._buffer];
    } else {
      delete this.requesterConfig['items'];
    }

    if (
      (this.requesterConfig.offset !== this._currentOffset) ||
      forceEmit ||
      (this._currentOffset === this.requesterConfig.offset && this._currentOffset === 0)
    ) {
      this._currentOffset = this.requesterConfig.offset;
      this.loading = true;
      this._changeDetector.markForCheck();
      // this.reloadTable(true);
      this._tablelistDataHandlerService.requestEmitter(this.requesterConfig);
    }
  }

  private setSelectedItem(checked: boolean) {
    if (!checked) {
      this.selectedRows = [];
    } else {
      if (this._unselectedRowsIds && this._unselectedRowsIds.length > 0) {
        const loadedItemsAllPagesFiltered = this.loadedItemsAllPages.filter((selectedRow) => {
          return this._unselectedRowsIds.filter(unselectedRowId => {
            return selectedRow.id === unselectedRowId;
          }).length === 0;
        });

        this.selectedRows = [...loadedItemsAllPagesFiltered];
      } else {
        this.selectedRows = [...this.loadedItemsAllPages];
      }
    }

    this._dynamicApiService.setWorklistSelectedRows(this.selectedRows);
    this.selectAllRowsClicked.emit(checked);
  }

  private toggleCommonDocumentActivation(action) {
    this._workflowService.toggleCommonDocumentState(action.rowData.id)
      .subscribe(
        (state) => {
          const statusStr: string = state.body.newState ? 'activated' : 'deactivated';
          this.notificationMessageService.success(`Document ${action.rowData.friendlyName} ${statusStr} successfully`);
          this._userActionCreatorService.setReloadAdminWorklist(true);
        },
        () => {
          this.notificationMessageService.error('Unable to change activation status of Document');
        }
      );

  }

  private downloadCommonDocument(action) {
    this._coreActionCreator.setLoader(true);
    this._workflowService.getReportData(action.rowData.id).subscribe((file: ExtendedHttpResponse<any> | any) => {
      // decode base64 response
      const byteString = atob(file.data ? file.data : file.body);
      const buffer = new ArrayBuffer(byteString.length);
      let unicodeArray = new Uint8Array(buffer);
      for (let i = 0; i < byteString.length; i++) {
        unicodeArray[i] = byteString.charCodeAt(i);
      }
      // end decode

      const blob = new Blob([unicodeArray], {type: "application/*"});
      const url = window.URL;
      const link = url.createObjectURL(blob);
      const element = document.createElement("a");
      element.download = action.rowData.friendlyName;
      element.href = link;
      document.body.appendChild(element);
      element.click();

      this._coreActionCreator.setLoader(false);
      url.revokeObjectURL(link);
      document.body.removeChild(element);
    }, () => {
      this._coreActionCreator.setLoader(false);
      this.notificationMessageService.error('Unable to download file');
    });
  }

  private popupForm(action, actionData: ITablelistAction) {
    // since that it is required to have reference of both the window & worklist objects, the method is bind to
    // an object that have both references
    const bindObj: any = this;
    // Popups a form according to a configurable injected action
    const actionPayload: any = actionData['payload'];
    const actionCallbackClassName = actionData['key'];

    if (!actionPayload) {
      console.error('Injected Action does not have a payload. WorklistTemplate needs to be updated.');
      return;
    }

    if (!actionPayload.type) {
      console.error('Injected Action does not have a type. WorklistTemplate needs to be updated.');
      return;
    }

    switch (actionPayload.type) {
      case 'COMMON_DOCUMENT_ACTIVATION':
        bindObj.worklist.toggleCommonDocumentActivation(action);
        break;

      case 'DOWNLOAD-COMMON_DOCUMENT':
        bindObj.worklist.downloadCommonDocument(action);
        break;

      case 'FORM_MODAL':
        if (!actionPayload.formId) {
          console.error('Injected Action does not have a formId. WorklistTemplate needs to be updated.');
          return;
        }

        const formData = {};

        if (_.get(action, 'rowData.Patient')) {
          formData['patientId'] = action.rowData.Patient.id;
        }

        formData['id'] = action.rowData.id;
        formData['meta'] = action.rowData.meta;

        if (actionPayload.data) {
          // Getting the list of parameters that should be updated via the popup form.
          // In the future, when 'interpolate' will be added to data binding, this won't be needed and all parameters wil be taken from the form config itself.
          actionPayload.data.forEach(item => {
            formData[item.fieldName] = _.get(action, `rowData.${item.fieldPath}`);
          });
        }

        action.formHandlerService.popupDynamicFormModal(
          actionPayload.formTitle,
          actionPayload.formId,
          formData,
          actionPayload.approveButton ? actionPayload.approveButton : 'Confirm',
          actionPayload.declineButton ? actionPayload.declineButton : 'Cancel',
          {'classes': actionPayload.class ? actionPayload.class + ' ' : ''},
          this._modalLoaderTunnelName,
          actionPayload.manualSave ? null : PopupActionComponent,
          false
        )
          .take(1).subscribe((fromData) => {
            if (_.get(fromData, 'status') === modalResponse.approve) {
              const resolvedCallbackClass = bindObj.worklist.injectedActionsFactoryService.getClass(
                actionCallbackClassName
              );

              if (resolvedCallbackClass) {
                resolvedCallbackClass.performActionAfterConfirm(
                  fromData,
                  bindObj,
                  action
                );
              } else {
                console.error(
                  'The actionCallbackClassName is not registered in the callbackClasses map!'
                );
              }
            }
          });
        break;
      case 'CONFIRM_MODAL':
        action.modalLoaderService.popupModal(
          ConfirmModalComponent,
          ConfirmModalLoaderPayload.builder()
            .setTitle(action._translate.instant(actionPayload.title) || 'Confirm')
            .setContent(action._translate.instant(actionPayload.content) || 'Are you sure you want to make this action?')
            .setApproveButtonText(actionPayload.approveButton)
            .setDeclineButtonText(actionPayload.declineButton),
          ModalTypes.ALERT
        )
          .subscribe(response => {
            if (_.get(response, 'payload') === modalResponse.approve) {
              const resolvedCallbackClass = bindObj.worklist.injectedActionsFactoryService.getClass(
                actionCallbackClassName
              );
              if (resolvedCallbackClass) {
                resolvedCallbackClass.performActionAfterConfirm(
                  bindObj,
                  action
                );
              } else {
                console.error(
                  'The actionCallbackClassName is not registered in the callbackClasses map!'
                );
              }
            }
          });
        break;
      case 'RETRIGGER_ACTIVATION':
      case 'ACCESS-ORGANIZATION-REPORT':
        const resolvedCallbackClass = bindObj.worklist.injectedActionsFactoryService.getClass(
          actionCallbackClassName
        );

        if (resolvedCallbackClass) {
          resolvedCallbackClass.performActionAfterConfirm(
            bindObj,
            action
          );
        }
        break;
      default:
        console.warn('Injected Action type was not found. Check WorklistTemplate. Type = ' + actionPayload.type);
    }
  }

  private reloadWorklistTableConfig(): void {
    this._dynamicApiService.getReloadWorklistStatus()
      .pipe(
        takeUntil(this._destroyer$.asObservable())
      )
      .subscribe((status) => {
        if (status) {
          this.reloadTable(true);
          this.reloadTriggered.emit(true);
          this._tablelistStoreHandlerService
            .clear(this.requesterConfig.resourceType, this.requesterConfig.configTemplateType)
            .pipe(
              takeUntil(this._destroyer$.asObservable()),
            )
            .subscribe();
          this.selectedRows = [];
          this.setWorklist(true);
        }
      });
  }

  private updateDynamicSearchConfig(config: any): any {
    const dynamicSearch = _.get(config, 'dynamicSearch', undefined);
    const adaptableFilters = _.get(config, 'adaptableFilters.dynamicSearch', undefined);

    if (!!adaptableFilters && !!dynamicSearch) {
      // idQuery
      if (_.has(adaptableFilters, 'idQuery')) {
        _.set(
          dynamicSearch,
          'idQuery.filters',
          this.collectFilters(_.get(dynamicSearch, 'idQuery.filters', []), _.get(adaptableFilters, 'idQuery.filters', []))
        );
      }

      if (_.has(adaptableFilters, 'dataQuery')) {
        _.set(
          dynamicSearch,
          'dataQuery.filters',
          this.collectFilters(_.get(dynamicSearch, 'dataQuery.filters', []), _.get(adaptableFilters, 'dataQuery.filters', []))
        );
      }

      // dataQuery
      config.dynamicSearch = _.cloneDeep(dynamicSearch);
    }

    return config;
  }

  private collectFilters(srcFilters: any[], adjFilters: any[]): any[] {
    if (adjFilters.length > 0) {
      const newFilters = [];

      adjFilters.forEach(f => {
        newFilters.push(this._adaptableFilterService.getFilter(f));
      });

      return srcFilters.concat(newFilters);
    }

    return srcFilters;
  }

  // Table from adaptableFilters descriptor should contain additional  to mandatory columns, mandatory fields are defined
  // in default table, given in worklist template. Each mandatory column should have suitable value in 'mandatory' field
  // if no one of default columns is defined as a mandatory, all fields are taken from adaptiveFilters table
  private updateTitles() {
    this.titles = this.localConfig.table;

    if (!!this.appliedFilterProperties && !!this.requesterConfig.adaptableFilters) {
      const filterName = this._adaptableFilterService.getCurrentFilterName(this.appliedFilterProperties);

      if (_.has(this.requesterConfig.adaptableFilters, 'filterPanel')) {
        this.requesterConfig.adaptableFilters['filterPanel'].map((filterDescr) => {
          if (filterDescr.name === filterName && _.has(filterDescr, 'table')) {
            const mandatoryColumns = _.filter(this.titles, item => _.get(item, 'mandatory', false));
            this.titles = _.concat(mandatoryColumns, _.get(filterDescr, 'table'));
          }
        });
      }
    }
  }

  private getUseMultiSearch(): boolean {
    return _.get(this.requesterConfig, 'useMultipleSearch', false) || _.get(this.localConfig, 'useMultiSearch', false);
  }

  private getUseExactMultiSearch(): boolean {
    return _.get(this.requesterConfig, 'exactMultiSearch', false) || _.get(this.localConfig, 'exactMultiSearch', false);
  }

  private getCompactedSearchText(searchText: string): string {
    let newSearchText = searchText.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
    return StringUtils.compactArray(StringUtils.getChunks(newSearchText)).join(' ');
  }
}
